"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { COMMAND_ITEMS, type NavCommandItem } from "@/lib/navigation";
import { Search, ArrowRight, Inbox, Sparkles, Clock } from "lucide-react";
import { cn } from "@/lib/utils/cn";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RECENT_ITEMS = [
  { label: "HR Trends 2026", href: "/articles/hr-trends-2026", category: "Articles" },
  { label: "Job Description Handbook", href: "/resources/templates/jd", category: "Templates" },
  { label: "DEI Best Practices", href: "/guides/dei-best-practices", category: "Guides" },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filtered = useMemo(() => {
    if (!query.trim()) return { navigation: COMMAND_ITEMS, results: [] };
    const q = query.toLowerCase().trim();
    const navResults = COMMAND_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.keywords?.some((k) => k.toLowerCase().includes(q)),
    );
    return { navigation: navResults, results: [] };
  }, [query]);

  const allItems = useMemo(() => filtered.navigation, [filtered.navigation]);

  const groupedItems = useMemo(() => {
    const groups = new Map<string, NavCommandItem[]>();
    allItems.forEach((item) => {
      const cat = item.category;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(item);
    });
    return groups;
  }, [allItems]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Tab") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : prev));
      } else if (e.key === "Enter" && allItems[selectedIndex]) {
        e.preventDefault();
        onOpenChange(false);
        router.push(allItems[selectedIndex].href);
      } else if (e.key === "Escape") {
        onOpenChange(false);
      }
    },
    [allItems, selectedIndex, router, onOpenChange],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={() => onOpenChange(false)}
      />
      <Command
        className="relative w-full max-w-xl mx-4 bg-card rounded-2xl border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border/60">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <CommandInput
            placeholder="Search commands, pages, content..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-none bg-transparent py-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[0.625rem] font-mono text-muted-foreground shrink-0">
            ESC
          </kbd>
        </div>

        <ScrollArea className="max-h-[60vh]">
          <CommandList>
            {allItems.length === 0 ? (
              <CommandEmpty className="py-10 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                    <Inbox className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">No results found</p>
                  <p className="text-xs text-muted-foreground/80">Try searching for a page, command, or topic</p>
                </div>
              </CommandEmpty>
            ) : (
              <>
                {!query.trim() && (
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recent</p>
                    <div className="space-y-0.5">
                      {RECENT_ITEMS.map((item) => (
                        <button
                          key={item.href}
                          onClick={() => {
                            onOpenChange(false);
                            router.push(item.href);
                          }}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-neutral-100"
                        >
                          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.category}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {!query.trim() && <CommandSeparator className="mx-3" />}

                {Array.from(groupedItems.entries()).map(([category, items]) => (
                  <CommandGroup key={category}>
                    {items.map((item, index) => {
                      const currentIndex = allItems.indexOf(item);
                      return (
                        <CommandItem
                          key={item.href}
                          value={item.label}
                          onSelect={() => {
                            onOpenChange(false);
                            router.push(item.href);
                          }}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 cursor-pointer",
                            currentIndex === selectedIndex && "bg-muted/30",
                          )}
                          aria-selected={currentIndex === selectedIndex}
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-muted-foreground">
                            <item.icon className="h-4 w-4" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{item.label}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                          </div>
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0" />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}

                {allItems.length > 0 && (
                  <div className="px-3 py-2 border-t border-border/60 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-primary" />
                      Navigate with <kbd className="font-mono">&#8593;&#8595;</kbd> and <kbd className="font-mono">Enter</kbd>
                    </p>
                    <Badge variant="secondary" className="h-5 text-[0.625rem] font-mono">
                      {allItems.length} items
                    </Badge>
                  </div>
                )}
              </>
            )}
          </CommandList>
        </ScrollArea>
      </Command>
    </div>
  );
}